import {
  hasOnlyIncompleteTariffs,
  incompleteClaimFields,
  withIncompleteClaimValidation,
} from './incompleteClaim';
import { z } from 'zod';
import { baseClaimSchema } from './baseClaim';
import { providerSchema } from './provider';
import { siteSchema } from './site';
import {
  individualBulkClaimSchema,
  individualBulkClaimSaveSchema,
} from './individualBulkClaimSchema';
import { tariffSchema } from './tariff';
import { validateClaimLocationOfService } from './claimLocationOfService';
import { payorSchema } from './payor';
import { validateProvinceClaimFields } from './provinceClaimFields';

const sharedBulkClaimBaseSchema = baseClaimSchema
  .partial()
  .omit({
    claimNumber: true,
    updatedAt: true,
  })
  .extend({
    internalComments: z.string().optional().nullable(),
    serviceStart: z.string().optional().nullable(),
    createdAt: z.string(),
    serviceEnd: z.string(),
    patientID: z.uuid().nullable(),
    providerID: z.uuid().nullable(),
    provider: z
      .lazy(() => providerSchema)
      .optional()
      .nullable(),
    payor: z
      .lazy(() => payorSchema)
      .optional()
      .nullable(),
    site: z.lazy(() => siteSchema),
    referringProviderObject: z
      .lazy(() => providerSchema)
      .optional()
      .nullable(),
    tariffs: z.array(z.lazy(() => tariffSchema)).optional(),
  });

const sharedBulkClaimSchema = sharedBulkClaimBaseSchema
  .superRefine(validateClaimLocationOfService)
  .superRefine(validateProvinceClaimFields);

export const bulkClaimSchema = z.object({
  shared: sharedBulkClaimSchema,
  claims: z.array(z.lazy(() => individualBulkClaimSchema)),
});

export type bulkClaimType = z.infer<typeof bulkClaimSchema>;
export type sharedClaimType = z.infer<typeof bulkClaimSchema.shape.shared>;

const sharedBulkClaimSaveSchema = withIncompleteClaimValidation(
  sharedBulkClaimSchema,
  sharedBulkClaimBaseSchema.extend(incompleteClaimFields)
);

const sharedIncompleteBatchSchema = sharedBulkClaimBaseSchema.extend({
  ...incompleteClaimFields,
  // Shared tariffs are optional templates; individual claims determine save status.
  tariffs: z
    .array(
      incompleteClaimFields.tariffs.element.extend({
        status: tariffSchema.shape.status,
      })
    )
    .optional(),
});

const individualClaimsSaveSchema = z.array(individualBulkClaimSaveSchema);

export const bulkClaimSaveSchema = z
  .object({
    // Defer both sections to the transform so an invalid claim cannot skip shared validation.
    shared: z.unknown(),
    claims: z.unknown(),
  })
  .transform((data, ctx) => {
    const claims = individualClaimsSaveSchema.safeParse(data.claims);
    // Relax shared requirements only for a nonempty batch of entirely incomplete claims.
    const allClaimsIncomplete =
      Array.isArray(data.claims) &&
      data.claims.length > 0 &&
      data.claims.every(hasOnlyIncompleteTariffs);
    const sharedSchema = allClaimsIncomplete
      ? sharedIncompleteBatchSchema
      : sharedBulkClaimSaveSchema;
    // Parse shared fields even when individual claims failed, so both sets of errors are shown.
    const shared = sharedSchema.safeParse(data.shared);
    // Restore the section prefixes so form errors point to their original nested fields.
    if (!claims.success) {
      claims.error.issues.forEach((issue) => {
        ctx.addIssue({ ...issue, path: ['claims', ...issue.path] });
      });
    }
    if (!shared.success) {
      shared.error.issues.forEach((issue) => {
        ctx.addIssue({ ...issue, path: ['shared', ...issue.path] });
      });
    }
    // The issues above mark parsing as failed. z.NEVER exits the transform and has
    // TypeScript type `never`, so it adds no alternative to the successful output type.
    if (!claims.success || !shared.success) return z.NEVER;
    // Only validated, schema-normalized values leave this boundary.
    return { shared: shared.data, claims: claims.data };
  });
